import type { LexicalContent } from "@/lib/cms"
import { assertRoundTrippable } from "./payload-lexical-nodes"

/**
 * True when `value` is a well-formed serialized Lexical document: an object with
 * a `root` that has a `children` array. `assertRoundTrippable` only screens node
 * *types*, so it returns ok for `null`/`""`/`{}`/a bare string — none of which
 * `parseEditorState` can consume. This is the structural gate that runs first.
 */
export function isLexicalTree(value: unknown): value is LexicalContent {
  if (value === null || typeof value !== "object") return false
  const root = (value as { root?: unknown }).root
  if (root === null || typeof root !== "object") return false
  return Array.isArray((root as { children?: unknown }).children)
}

/**
 * An empty but editable Lexical document — one root with a single empty
 * paragraph. Seeded when a body was never written (the field comes back
 * `null`/empty), so the editor opens blank and editable rather than crashing.
 */
export function emptyLexicalDocument(): LexicalContent {
  // `format`/`indent`/`version`/`direction` are omitted — Lexical fills element
  // defaults on parse (unformatted element `format` is `""`), so the seed only
  // needs the structural shape `parseEditorState` requires. The editor
  // re-serializes the canonical node on save.
  return {
    root: {
      type: "root",
      children: [{ type: "paragraph", children: [] }],
    },
  }
}

/**
 * The modal's load decision for a fetched field value, run before mounting the
 * editor:
 * - `null`/empty (`undefined`, `null`, `""`) → seed an empty editable document
 *   (a never-written body is a legitimate edit target, not an error).
 * - a present-but-malformed value (string, object without a `root.children`
 *   array) → fail loud (don't mount; offer the "edit in the CMS" path), same as
 *   an unsupported node.
 * - a well-formed tree → run `decideLexicalGuard` (unsupported-node screen) and
 *   surface its decision; on ok, mount seeded with the tree.
 */
export type LexicalLoadDecision =
  | { kind: "ready"; tree: LexicalContent }
  | { kind: "unsupported"; unsupported: string[] }
  | { kind: "malformed" }

export function decideLexicalLoad(value: unknown): LexicalLoadDecision {
  if (value === undefined || value === null || value === "") {
    return { kind: "ready", tree: emptyLexicalDocument() }
  }
  if (!isLexicalTree(value)) {
    return { kind: "malformed" }
  }
  const guard = decideLexicalGuard(value)
  if (guard.kind === "unsupported") {
    return { kind: "unsupported", unsupported: guard.unsupported }
  }
  return { kind: "ready", tree: value }
}

/**
 * The modal's render decision for a loaded Lexical tree. The editor only mounts
 * when every node round-trips; otherwise it refuses (fail loud) so a lossy save
 * is impossible. Factored out of the modal so the decision is unit-testable
 * without React. (AHD-7515)
 */
export type LexicalGuardDecision =
  | { kind: "ok" }
  | { kind: "unsupported"; unsupported: string[] }

export function decideLexicalGuard(tree: unknown): LexicalGuardDecision {
  const result = assertRoundTrippable(tree)
  if (result.ok) return { kind: "ok" }
  return { kind: "unsupported", unsupported: result.unsupported }
}

/**
 * Derive the Payload admin doc URL from the public CMS API URL. The admin link
 * is shown ONLY in the fail-loud "edit in the CMS" branch — exactly when the
 * user must use it — so it must resolve for any sane CMS URL, not just one that
 * ends in `/api`. We strip a trailing `/api` (or `/api/`) when present, else use
 * the URL's origin, then point at
 * `<base>/admin/collections/<collection>/<id>`. Returns null only when the URL
 * is absent or unparseable.
 */
export function cmsAdminDocUrl(
  cmsApiUrl: string | undefined,
  collection: string,
  id: string
): string | null {
  if (!cmsApiUrl) return null
  const trimmed = cmsApiUrl.replace(/\/+$/, "")
  const base = trimmed.endsWith("/api")
    ? trimmed.slice(0, -"/api".length)
    : originOf(trimmed)
  if (!base) return null
  return `${base}/admin/collections/${encodeURIComponent(collection)}/${encodeURIComponent(id)}`
}

/** The scheme+host of a URL string, or null if it can't be parsed. */
function originOf(url: string): string | null {
  const parsed = URL.canParse(url) ? new URL(url) : null
  return parsed ? parsed.origin : null
}
